import uuid from 'react-uuid';
import {
  ActiveRecordingBlockInput,
  MicState,
  TranscriptBlockInput,
} from 'woundscribe/types';
import { WOUNDSCRIBE_MIC_STATES } from './constants';

export function getMicStatusLabel(state: MicState): string {
  switch (state) {
    case WOUNDSCRIBE_MIC_STATES.RECORDING:
      return 'Rec';
    case WOUNDSCRIBE_MIC_STATES.PAUSED:
      return 'Paused';
    case WOUNDSCRIBE_MIC_STATES.PROCESSING:
      return 'Wait';
    case WOUNDSCRIBE_MIC_STATES.IDLE:
    default:
      return '';
  }
}

const STATUS_TOAST_PATTERN =
  /permission|denied|cannot resume|disconnected|locked|backgrounded/i;

export function getMicBottomBarSubtitle(
  state: MicState,
  toast: string | null,
  liveTranscript?: string | null
): string {
  if (toast && STATUS_TOAST_PATTERN.test(toast)) {
    return toast;
  }

  const live = liveTranscript?.trim();
  if (
    live &&
    (state === WOUNDSCRIBE_MIC_STATES.RECORDING ||
      state === WOUNDSCRIBE_MIC_STATES.PAUSED)
  ) {
    return live;
  }

  switch (state) {
    case WOUNDSCRIBE_MIC_STATES.IDLE:
      return toast || 'Start recording';
    case WOUNDSCRIBE_MIC_STATES.RECORDING:
      return toast || 'Recording';
    case WOUNDSCRIBE_MIC_STATES.PAUSED:
      return toast || 'Paused';
    case WOUNDSCRIBE_MIC_STATES.PROCESSING:
      return toast || 'Saving recording…';
    default:
      return toast || '';
  }
}

export function getMicPauseResumeLabels(state: MicState): {
  ariaLabel: string;
  title: string;
} {
  switch (state) {
    case WOUNDSCRIBE_MIC_STATES.RECORDING:
      return { ariaLabel: 'Pause recording', title: 'Pause' };
    case WOUNDSCRIBE_MIC_STATES.PAUSED:
      return { ariaLabel: 'Resume recording', title: 'Resume' };
    default:
      return { ariaLabel: 'Toggle pause', title: 'Toggle' };
  }
}

export function getMicStopLabels(
  state: MicState,
  elapsedLabel: string
): { ariaLabel: string; title: string } {
  if (state === WOUNDSCRIBE_MIC_STATES.PROCESSING) {
    return { ariaLabel: 'Processing', title: 'Saving…' };
  }
  return {
    ariaLabel: `Stop recording ${elapsedLabel}`,
    title: 'Stop',
  };
}

/** True when URL is inside a specific encounter (`.../encounter/{id}/...`). */
export function pathHasEncounter(pathname: string): boolean {
  return /\/encounter\/[^/]+/i.test(pathname);
}

/** True when URL is inside woundscribeTab withing specific encounter (`.../{id}/woundscribe/...`). */
export function pathHasWoundScribe(pathname: string): boolean {
  return /\/woundscribe(\/|$)/i.test(pathname);
}

/**
 * Client-generated id for one recording take, created before any socket
 * connection is attempted. This is what groups a take's audio chunks in
 * IndexedDB even when the backend session id never arrives.
 */
export function generateLocalRecordingId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `ws-local-${Date.now()}-${uuid()}`;
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function getEncounterIdFromPath(pathname: string): string | undefined {
  const match = /\/encounter\/([^/]+)/i.exec(pathname);
  const id = match?.[1];
  // Ignore patient menu segment `/patients/:id/encounter` (list, no id).
  if (!id || id === 'new') {
    return undefined;
  }
  return id;
}

/** Patient id from `/patients/{id}/...`. */
export function getPatientIdFromPath(pathname: string): string | undefined {
  const match = /\/patients\/([^/]+)/i.exec(pathname);
  const id = match?.[1];
  if (!id || id === 'new') {
    return undefined;
  }
  return id;
}

/**
 * Encounter page the user uploaded from, without the WoundScribe tab.
 * Empty when the current page is not that encounter.
 */
export function woundscribeEncounterBase(pathname: string): string {
  const index = pathname.indexOf('/woundscribe');
  if (index > 0) {
    return pathname.slice(0, index);
  }
  return '';
}

/** Completed Recording for a saved encounter. Null when that encounter is unknown. */
export function transcriptReadyPath(
  returnPath: string | undefined,
  pathname: string
): string | null {
  const base = returnPath || woundscribeEncounterBase(pathname);
  if (!base) {
    return null;
  }
  return `${base}/woundscribe/complete-recording`;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  );
}

/**
 * Whether existing or just-finished transcripts block a new recording (conditions
 * 7, 8, and 9). Applies only when the LD flag `ws-allow-multiple-recordings` is off.
 * A loading or failed fetch blocks too, because we cannot tell whether a transcript
 * already exists (fail closed). The Start tab and the idle footer both use this, so
 * they cannot drift apart. See "One Transcript per Encounter" in woundscribe/CLAUDE.md.
 */
export function isStartBlockedByTranscripts({
  allowMultipleRecordings,
  hasCompletedRecording,
  existingTranscriptCount,
  isLoadingExistingTranscripts,
  hasFetchError,
}: TranscriptBlockInput): boolean {
  return (
    !allowMultipleRecordings &&
    (hasCompletedRecording ||
      existingTranscriptCount > 0 ||
      isLoadingExistingTranscripts ||
      hasFetchError)
  );
}

/**
 * Whether a recording already in flight elsewhere, or unfinished local audio from a
 * previous attempt, blocks a new recording on this encounter. Unlike
 * isStartBlockedByTranscripts, this is NOT gated by the ws-allow-multiple-recordings flag:
 * at most one live recording or upload may ever be in flight for an encounter at a time,
 * independent of how many finished transcripts the flag allows.
 *
 * Unknown/loading fails closed, the same philosophy as a failed existing-transcripts fetch:
 * we cannot yet prove it is safe to start, so Start Recording stays disabled rather than
 * racing a check that has not resolved.
 */
export function isStartBlockedByActiveRecording({
  recordingEligibility,
  isLoadingRecordingEligibility,
  hasLocalOrphanedRecordings,
}: ActiveRecordingBlockInput): boolean {
  if (hasLocalOrphanedRecordings) {
    return true;
  }
  if (isLoadingRecordingEligibility || !recordingEligibility) {
    return true;
  }
  return recordingEligibility.canStart === false;
}
